import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../utils/api';
import { AlertTriangle, Banknote, ClipboardList, Calendar, Clock, Landmark, CreditCard, Loader2, ScrollText, ShieldCheck } from 'lucide-react';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const fmtHour = (h) => {
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:00 ${period}`;
};

// Current day/hour in IST, regardless of the browser's local timezone —
// mirrors the same calculation the backend uses to enforce the window.
const getIstNow = () => {
  const now = new Date();
  const utcMs = now.getTime() + now.getTimezoneOffset() * 60000;
  return new Date(utcMs + 5.5 * 60 * 60000);
};

export default function Withdraw() {
  const { user } = useAuth();
  const [withdrawals, setWithdrawals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ amount: '', walletType: 'roi', payoutMethod: 'INR' });
  const [wSettings, setWSettings] = useState({ withdrawalActive: true, withdrawalOffDay: 0, withdrawalStartHour: 0, withdrawalEndHour: 10 });

  useEffect(() => {
    Promise.all([
      api.get('/withdraw/my'),
      api.get('/income/summary')
    ]).then(([wr, sr]) => {
      setWithdrawals(wr.data);
      setSummary(sr.data);
    }).catch(console.error).finally(() => setLoading(false));
    api.get('/settings/roi').then(r => setWSettings(r.data)).catch(console.error);
  }, []);

  const istNow = getIstNow();
  const istDay = istNow.getDay();
  const istHour = istNow.getHours();
  const windowClosedReason = !wSettings.withdrawalActive
    ? 'Withdrawals are currently paused by admin. Please try again later.'
    : istDay === wSettings.withdrawalOffDay
      ? `Withdrawals are not available on ${DAY_NAMES[istDay]}. Please try again on a working day.`
      : (istHour < wSettings.withdrawalStartHour || istHour >= wSettings.withdrawalEndHour)
        ? `Withdrawal requests are only accepted between ${fmtHour(wSettings.withdrawalStartHour)} and ${fmtHour(wSettings.withdrawalEndHour)} (IST).`
        : null;
  const windowOpen = !windowClosedReason;

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amount = Number(form.amount);
    if (!amount || amount < 200) return toast.error('Minimum withdrawal ₹200');
    if (amount % 100 !== 0) return toast.error('Amount must be in multiples of ₹100');

    if (!windowOpen) {
      return toast.error(windowClosedReason);
    }

    if (user?.kycStatus !== 'approved') {
      return toast.error('Please complete your KYC verification before requesting a withdrawal');
    }

    if (form.payoutMethod === 'USDT') {
      if (!user?.bankDetails?.usdtAddress) {
        return toast.error('Please add your USDT wallet address in Profile first');
      }
    } else if (!user?.bankDetails?.accountNumber && !user?.bankDetails?.upiId) {
      return toast.error('Please update your bank/UPI details in Profile first');
    }

    setSubmitting(true);
    try {
      const res = await api.post('/withdraw/request', form);
      toast.success(res.data.message);
      setWithdrawals(prev => [res.data.withdrawal, ...prev]);
      setForm({ amount: '', walletType: 'roi', payoutMethod: 'INR' });
      const sr = await api.get('/income/summary');
      setSummary(sr.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (n) => `₹${(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const deduction = form.amount ? (Number(form.amount) * 0.10).toFixed(2) : 0;
  const netAmount = form.amount ? (Number(form.amount) * 0.90).toFixed(2) : 0;

  const walletOptions = [
    { key: 'roi', label: 'ROI Wallet', value: summary?.roiWallet },
    { key: 'direct', label: 'Direct Wallet', value: summary?.directWallet },
    { key: 'level', label: 'Level Wallet', value: summary?.levelWallet },
    { key: 'royalty', label: 'Royalty Wallet', value: summary?.royaltyWallet },
    { key: 'reward', label: 'Reward Wallet', value: summary?.rewardWallet },
    { key: 'bonus', label: 'Bonus Wallet', value: summary?.walletBalance },
  ];

  return (
    <DashboardLayout>
      <h1 className="page-title">Withdraw</h1>
      <p className="page-subtitle">Request withdrawal from your earnings</p>

      {user?.kycStatus !== 'approved' && (
        <div className="alert alert-warning" style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
          <ShieldCheck size={16} />
          {user?.kycStatus === 'pending'
            ? <>Your KYC is under review. Withdrawals will unlock once it's approved.</>
            : user?.kycStatus === 'rejected'
              ? <>Your KYC was rejected. Please <Link to="/dashboard/kyc" style={{ color: 'var(--primary)', fontWeight: 700 }}>re-submit your KYC</Link> to unlock withdrawals.</>
              : <>Please complete <Link to="/dashboard/kyc" style={{ color: 'var(--primary)', fontWeight: 700 }}>KYC verification</Link> before requesting a withdrawal.</>}
        </div>
      )}

      {!user?.bankDetails?.accountNumber && !user?.bankDetails?.upiId && !user?.bankDetails?.usdtAddress && (
        <div className="alert alert-warning" style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertTriangle size={16} /> Please <Link to="/dashboard/profile" style={{ color: 'var(--primary)', fontWeight: 700 }}>update your bank/UPI or USDT details</Link> before withdrawing.
        </div>
      )}

      {!windowOpen && (
        <div className="alert alert-warning" style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Clock size={16} /> {windowClosedReason}
        </div>
      )}

      {/* Wallet Balances */}
      {summary && (
        <div className="wallet-grid" style={{ marginBottom: 24 }}>
          {walletOptions.map(w => (
            <div key={w.key} className="wallet-card" style={{ cursor: 'pointer', border: form.walletType === w.key ? '1px solid var(--primary)' : undefined }}
              onClick={() => setForm(f => ({ ...f, walletType: w.key }))}>
              <div className="wallet-label">{w.label}</div>
              <div className="wallet-value" style={{ color: 'var(--primary)' }}>{formatCurrency(w.value)}</div>
            </div>
          ))}
        </div>
      )}

      <div className="grid-2" style={{ gap: 24, marginBottom: 28 }}>
        {/* Withdraw Form */}
        <div className="card">
          <div className="card-header"><h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Banknote size={18} /> Request Withdrawal</h3></div>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Select Wallet</label>
              <select name="walletType" className="form-control" value={form.walletType} onChange={handleChange}>
                {walletOptions.map(w => (
                  <option key={w.key} value={w.key}>{w.label} — {formatCurrency(w.value)}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Payout Method</label>
              <select name="payoutMethod" className="form-control" value={form.payoutMethod} onChange={handleChange}>
                <option value="INR">INR — Bank Transfer / UPI</option>
                <option value="USDT">USDT — Crypto Wallet</option>
              </select>
              {form.payoutMethod === 'USDT' && !user?.bankDetails?.usdtAddress && (
                <small style={{ color: 'var(--primary)', fontSize: '0.78rem' }}>
                  No USDT address on file — <Link to="/dashboard/profile" style={{ color: 'var(--primary)', fontWeight: 700 }}>add one in Profile</Link> first.
                </small>
              )}
            </div>
            <div className="form-group">
              <label>Amount (INR) *</label>
              <div className="input-group">
                <span className="input-icon">₹</span>
                <input
                  type="number"
                  name="amount"
                  className="form-control"
                  placeholder="Min ₹200, multiples of ₹100"
                  value={form.amount}
                  onChange={handleChange}
                  min={200} step={100}
                  required
                />
              </div>
            </div>

            {form.amount > 0 && (
              <div style={{ background: 'rgba(var(--primary-rgb),0.05)', border: '1px solid rgba(var(--primary-rgb),0.2)', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ color: 'var(--text-muted)' }}>Requested Amount</span>
                  <span style={{ color: 'var(--text-white)' }}>{formatCurrency(Number(form.amount))}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ color: 'var(--text-muted)' }}>Deduction (10%)</span>
                  <span style={{ color: '#ef4444' }}>- ₹{deduction}</span>
                </div>
                <hr style={{ border: 'none', borderTop: '1px solid rgba(var(--primary-rgb),0.2)', margin: '8px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 700, color: 'var(--text-white)' }}>Net Amount</span>
                  <span style={{ fontWeight: 700, color: '#22c55e', fontSize: '1.1rem' }}>₹{netAmount}</span>
                </div>
              </div>
            )}

            <button type="submit" className="btn btn-primary btn-block" disabled={submitting || user?.kycStatus !== 'approved' || !windowOpen || (form.payoutMethod === 'USDT' && !user?.bankDetails?.usdtAddress)}>
              {submitting ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Loader2 size={16} className="spin" /> Submitting...</span>
              ) : user?.kycStatus !== 'approved' ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><ShieldCheck size={16} /> Complete KYC to Withdraw</span>
              ) : !windowOpen ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Clock size={16} /> Withdrawal Window Closed</span>
              ) : (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Banknote size={16} /> Request Withdrawal</span>
              )}
            </button>
          </form>
        </div>

        {/* Rules */}
        <div className="card">
          <div className="card-header"><h3 className="card-title">📋 Withdrawal Rules</h3></div>
          <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 14 }}>
            {[
              { icon: '📅', text: `Withdrawal available all days except ${DAY_NAMES[wSettings.withdrawalOffDay]}` },
              { icon: '₹', text: 'Minimum withdrawal: ₹200 (multiples of ₹100)' },
              { icon: '%', text: '10% deduction on all withdrawals' },
              { icon: '⏰', text: `Submit: ${fmtHour(wSettings.withdrawalStartHour)} to ${fmtHour(wSettings.withdrawalEndHour)} (IST)` },
              { icon: '🏦', text: 'Bank credit time: 2:00 PM same day' },
              { icon: '💳', text: 'Supports INR & USDT payments' },
            ].map(r => (
              <li key={r.text} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <span style={{ background: 'rgba(var(--primary-rgb),0.1)', width: 30, height: 30, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '0.9rem' }}>{r.icon}</span>
                <span style={{ color: 'var(--text-light)', fontSize: '0.9rem', paddingTop: 5 }}>{r.text}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Withdrawal History */}
      <div className="card">
        <div className="card-header"><h3 className="card-title">📜 Withdrawal History</h3></div>
        {loading ? <div className="loader-wrap"><div className="loader"></div></div> :
          withdrawals.length === 0 ? (
            <div className="empty-state"><div className="empty-icon">💸</div><h3>No withdrawals yet</h3></div>
          ) : (
            <div className="table-wrap">
              <table className="tp-table">
                <thead>
                  <tr><th>Date</th><th>Requested</th><th>Deduction</th><th>Net Amount</th><th>Wallet</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {withdrawals.map(w => (
                    <tr key={w._id}>
                      <td style={{ fontSize: '0.85rem' }}>{new Date(w.createdAt).toLocaleDateString('en-IN')}</td>
                      <td style={{ color: 'var(--primary)', fontWeight: 700 }}>{formatCurrency(w.amount)}</td>
                      <td style={{ color: '#ef4444' }}>- {formatCurrency(w.deduction)}</td>
                      <td style={{ color: '#22c55e', fontWeight: 700 }}>{formatCurrency(w.netAmount)}</td>
                      <td style={{ textTransform: 'capitalize', fontSize: '0.85rem' }}>{w.walletType}</td>
                      <td>
                        <span className={`badge badge-${w.status === 'paid' ? 'success' : w.status === 'approved' ? 'info' : w.status === 'rejected' ? 'danger' : 'warning'}`}>
                          {w.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </div>
    </DashboardLayout>
  );
}